import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { UserCog, Menu, X, Briefcase } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import WhatsAppButton from '@/components/ui/WhatsAppButton';

interface HeaderProps {
  variant?: 'default' | 'blog';
}

const Header: React.FC<HeaderProps> = ({ variant = 'default' }) => {
  const { user, isAdmin, userRole } = useAuth();
  const isPartner = user && (userRole === 'representante' || userRole === 'afiliado');
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const location = useLocation();

  const isBlog = variant === 'blog';
  
  // Handle navigation - if on blog, use full URLs for anchor links
  const getLink = (anchor: string) => {
    if (isBlog) {
      return `/${anchor}`;
    }
    return anchor;
  };

  const handleLinkClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg"
      >
        Pular para o conteúdo
      </a>
      <header className="site-header fixed top-0 left-0 right-0 z-50 border-b bg-white/80 backdrop-blur-md shadow-sm">
        <div className="container mx-auto max-w-6xl py-1.5 px-4 md:px-6 flex justify-between items-center">
        <Link to="/" onClick={handleLinkClick}>
          <img 
            src="/lovable-uploads/8cb82dc5-bab6-4fd8-bab0-219185db8742.png" 
            alt="Logo TaxHealth (monograma circular)" 
            className="h-10 md:h-14 w-auto"
            width={56}
            height={56}
            fetchPriority="high"
          />
        </Link>
        
        {isMobile ? (
          <>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
              className="p-3 -mr-2 focus:outline-none min-h-[44px] min-w-[44px] flex items-center justify-center" 
              aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-gray-700" />
              ) : (
                <Menu className="h-6 w-6 text-gray-700" />
              )}
            </button>
            
            {mobileMenuOpen && (
              <div className="absolute top-full left-0 right-0 bg-white shadow-md p-4 flex flex-col space-y-2 animate-fade-in">
                {isBlog ? (
                  <Link
                    to="/#sobre-taxhealth"
                    onClick={handleLinkClick}
                    className="text-gray-700 hover:text-primary min-h-[44px] flex items-center px-2 py-2 rounded transition-colors"
                  >
                    Sobre a Tax Health
                  </Link>
                ) : (
                  <a
                    href="#sobre-taxhealth"
                    onClick={handleLinkClick}
                    className="text-gray-700 hover:text-primary min-h-[44px] flex items-center px-2 py-2 rounded transition-colors"
                  >
                    Sobre a Tax Health
                  </a>
                )}

                {isBlog ? (
                  <Link
                    to="/#equiparacao-hospitalar"
                    onClick={handleLinkClick}
                    className="text-gray-700 hover:text-primary min-h-[44px] flex items-center px-2 py-2 rounded transition-colors"
                  >
                    Equiparação Hospitalar
                  </Link>
                ) : (
                  <a
                    href="#equiparacao-hospitalar"
                    onClick={handleLinkClick}
                    className="text-gray-700 hover:text-primary min-h-[44px] flex items-center px-2 py-2 rounded transition-colors"
                  >
                    Equiparação Hospitalar
                  </a>
                )}

                {isBlog ? (
                  <Link
                    to="/#contabilidade"
                    onClick={handleLinkClick}
                    className="text-gray-700 hover:text-primary min-h-[44px] flex items-center px-2 py-2 rounded transition-colors"
                  >
                    Contabilidade
                  </Link>
                ) : (
                  <a
                    href="#contabilidade"
                    onClick={handleLinkClick}
                    className="text-gray-700 hover:text-primary min-h-[44px] flex items-center px-2 py-2 rounded transition-colors"
                  >
                    Contabilidade
                  </a>
                )}

                <Link
                  to="/blog"
                  onClick={handleLinkClick}
                  className={`min-h-[44px] flex items-center px-2 py-2 rounded transition-colors ${
                    location.pathname.startsWith('/blog')
                      ? 'text-primary font-medium'
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  Blog
                </Link>
                
                <WhatsAppButton
                  text="Falar com especialista"
                  source="header_mobile"
                  variant="whatsapp"
                  size="sm"
                  className="justify-start w-full"
                />
                
                {user && isPartner && (
                  <Link to="/portal" onClick={handleLinkClick}>
                    <Button variant="outline" size="sm" className="flex items-center w-full justify-start">
                      <Briefcase className="h-4 w-4 mr-2" />
                      Meu Portal
                    </Button>
                  </Link>
                )}
                
                {user && isAdmin && (
                  <Link to="/admin" onClick={handleLinkClick}>
                    <Button variant="outline" size="sm" className="flex items-center w-full justify-start">
                      <UserCog className="h-4 w-4 mr-2" />
                      Admin
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center space-x-3">
            <nav className="hidden md:flex items-center space-x-3">
              {isBlog ? (
                <Link 
                  to="/#sobre-taxhealth" 
                  className="text-sm text-gray-700 hover:text-primary transition-colors"
                >
                  Sobre a Tax Health
                </Link>
              ) : (
                <a 
                  href="#sobre-taxhealth" 
                  className="text-sm text-gray-700 hover:text-primary transition-colors"
                >
                  Sobre a Tax Health
                </a>
              )}
              
              {isBlog ? (
                <Link
                  to="/#equiparacao-hospitalar"
                  className="text-sm text-gray-700 hover:text-primary transition-colors"
                >
                  Equiparação Hospitalar
                </Link>
              ) : (
                <a
                  href="#equiparacao-hospitalar"
                  className="text-sm text-gray-700 hover:text-primary transition-colors"
                >
                  Equiparação Hospitalar
                </a>
              )}

              {isBlog ? (
                <Link
                  to="/#contabilidade"
                  className="text-sm text-gray-700 hover:text-primary transition-colors"
                >
                  Contabilidade
                </Link>
              ) : (
                <a
                  href="#contabilidade"
                  className="text-sm text-gray-700 hover:text-primary transition-colors"
                >
                  Contabilidade
                </a>
              )}
              
              <Link 
                to="/blog" 
                className={`text-sm transition-colors ${
                  location.pathname.startsWith('/blog') 
                    ? 'text-primary font-medium' 
                    : 'text-gray-700 hover:text-primary'
                }`}
              >
                Blog
              </Link>
              
              <WhatsAppButton
                text="Falar com especialista"
                source="header_desktop"
                variant="whatsapp"
                size="sm"
                className="px-3"
              />
            </nav>
            
            {user && isPartner && (
              <Link to="/portal">
                <Button variant="outline" size="sm" className="flex items-center">
                  <Briefcase className="h-4 w-4 mr-2" />
                  Meu Portal
                </Button>
              </Link>
            )}
            
            {user && isAdmin && (
              <Link to="/admin">
                <Button variant="outline" size="sm" className="flex items-center">
                  <UserCog className="h-4 w-4 mr-2" />
                  Admin
                </Button>
              </Link>
            )}
          </div>
        )}
        </div>
      </header>
    </>
  );
};

export default Header;
